import './bwa.css';
import { useState } from 'react';
import { api, errorText } from '../../api';
import { fmtDate, monthName } from '../../lib/format';
import { eur, eurS } from '../../lib/money';
import { useToast } from '../../lib/toast';
import { useApi } from '../../lib/useApi';
import { BwaDialog } from './BwaDialog';
import type { BwaTabResponse, KanzleiBwa } from './types';

// Reiter „BWA“ beim Mandanten (FiBu und Admin): BWA hochladen mit Umsatz und Ergebnis je Monat, alle BWAs mit Datei,
// Entfernen. Je Jahr sieht der Mandant die neueste BWA als „Gewinn und Verlust“ in seiner App.
export function BwaTab({ clientId }: { clientId: string }) {
  const toast = useToast();
  const res = useApi<BwaTabResponse>(`/api/kanzlei/bwa/${clientId}`, { live: ['guv', 'clients'], clientId });
  const [dialog, setDialog] = useState(false);
  const [removing, setRemoving] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const d = res.data;
  if (!d) return res.error ? <p className="note">{res.error}</p> : <div className="spinner" aria-label="Lädt" />;

  const remove = async (b: KanzleiBwa) => {
    setBusy(true);
    try {
      await api.del(`/api/kanzlei/bwa/report/${b.id}`);
      setRemoving(null);
      await res.reload();
      toast(`Entfernt: ${b.title}`);
    } catch (err) {
      toast(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  // „alle Monate“, „nur Dezember, dazu die Summe“
  const detail = (b: KanzleiBwa) => {
    if (!b.gap) return b.toMonth === 1 ? 'Januar' : 'alle Monate';
    const known = b.months.flatMap((m) => (m ? [monthName(m.month)] : []));
    return known.length ? `nur ${known.join(', ')}, dazu die Summe` : 'nur die Summe';
  };

  return (
    <div className="bwa-k">
      <div className="card">
        <div className="ch">
          <h3>BWA</h3>
          <span>der Mandant sieht daraus Gewinn und Verlust in seiner App</span>
        </div>
        <div className="bwa-top">
          <button type="button" className="btn sm dark" onClick={() => setDialog(true)}>
            BWA hochladen
          </button>
        </div>
        {d.reports.length === 0 ? (
          <p className="note">Noch keine BWA hochgeladen.</p>
        ) : (
          <div className="tw">
            <table className="tbwa">
              <thead>
                <tr>
                  <th>BWA</th>
                  <th>Ergebnis seit Januar</th>
                  <th>Monatswerte</th>
                  <th>Datei</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {d.reports.map((b) => (
                  <tr key={b.id}>
                    <td>
                      <b>{b.title}</b>
                      <span className="sub">
                        {b.current ? <span className="pill p-ok">in der App</span> : 'ersetzt durch eine neuere BWA'}
                      </span>
                    </td>
                    <td className="num" data-l="Ergebnis seit Januar">
                      <b>{eurS(b.sum.result)}</b>
                      <span className="sub">
                        Umsatz {eur(b.sum.revenue)} · Ausgaben {eur(b.sum.costs)}
                      </span>
                    </td>
                    <td data-l="Monatswerte">{detail(b)}</td>
                    <td data-l="Datei">
                      <a href={`/api/kanzlei/bwa/report/${b.id}/file`} target="_blank" rel="noopener noreferrer">
                        📄 {b.file.name}
                      </a>
                      <span className="sub">
                        {fmtDate(b.uploadedAt)}
                        {b.uploadedBy ? ` · ${b.uploadedBy}` : ''}
                      </span>
                    </td>
                    <td className="bwa-act">
                      {removing === b.id ? (
                        <span className="bwa-ask">
                          <span className="note">
                            {b.title} entfernen? {b.current ? 'Der Mandant sieht dann wieder die vorige BWA des Jahres.' : ''}
                          </span>
                          <button type="button" className="btn sm dark" disabled={busy} onClick={() => remove(b)}>
                            Ja, entfernen
                          </button>
                          <button type="button" className="btn sm" onClick={() => setRemoving(null)}>
                            Abbrechen
                          </button>
                        </span>
                      ) : (
                        <button
                          type="button"
                          className="del"
                          onClick={() => setRemoving(b.id)}
                          aria-label={`${b.title} entfernen`}
                        >
                          Entfernen
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="note bwa-foot">
          Hochladen: die BWA als PDF oder Foto, dazu Umsatz und vorläufiges Ergebnis je Monat
          {d.ki ? ' (liest die KI aus, Sie prüfen)' : ''}. Je Jahr zählt die neueste BWA; eine korrigierte BWA einfach neu
          hochladen.
        </p>
      </div>
      {dialog && (
        <BwaDialog
          clientId={clientId}
          clientName={d.client.name}
          reports={d.reports}
          ki={d.ki}
          kiAus={d.kiAus}
          kiTest={d.kiTest}
          onClose={() => {
            setDialog(false);
            void res.reload();
          }}
        />
      )}
    </div>
  );
}
